import { memo, useId, useRef, useEffect, useMemo, useCallback } from 'react';
import ItemsScrollBar from '@wconcept/wconcept-ui-renewal/components/atoms/ItemsScrollBar';
import ItemsSwiper from '@wconcept/wconcept-ui-renewal/components/atoms/ItemsSwiper';
import SwiperNavigation from '@wconcept/wconcept-ui-renewal/components/molecules/SwiperNavigation';
import BasicBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/BasicBtn';
import CouponItem2 from '@wconcept/wconcept-ui-renewal/components/molecules/_item/CouponItem2';
import Button from '@wconcept/wconcept-ui-renewal/components/atoms/Button';
import Img from '@wconcept/wconcept-ui-renewal/components/atoms/Img';
import { IconArrowRight09 } from '@wconcept/wconcept-ui-renewal/components/atoms/svg';
import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import { getResizeImageUrl } from '@wconcept/wconcept-common/utils/domainUtil';
import { Swiper as SwiperClass } from 'swiper/types';
import * as S from './style';

interface CustomColorType {
  bgColor?: string;
  textColor?: string;
  pointColor?: string;
  emphasisTextColor?: string;
}

type ColorType = 'white' | 'black' | CustomColorType | string;

interface IProps {
  colorType?: ColorType;
  brandSubRef?: React.RefObject<SwiperClass | null>;
  keyVisualImgSrc?: string;
  keyVisualImgRs?: string;
  couponTitle?: string;
  couponDesc?: string;
  subBrandList?: React.JSX.Element | React.JSX.Element[];
  productList?: React.ReactNode;
  isDesktop?: boolean;
  isSubBrandTabReady?: boolean;
  selectedTabId?: string;
  onClickMore?: () => void;
  btnText?: string;
  onClickCoupon?: () => void;
  status?: '1' | '2' | '3';
  /** 값이 바뀌면 서브브랜드 스크롤을 처음으로 리셋 (날짜 탭 전환 시 사용) */
  scrollResetKey?: string | number;
}

const BrandSubSection2 = ({
  colorType = 'white',
  keyVisualImgSrc,
  keyVisualImgRs,
  couponTitle,
  couponDesc,
  subBrandList,
  productList,
  isDesktop,
  isSubBrandTabReady = true,
  selectedTabId,
  onClickMore,
  brandSubRef,
  btnText = '자세히 보기',
  onClickCoupon,
  status,
  scrollResetKey,
}: IProps) => {
  const isCustomColor = typeof colorType === 'object';
  const customColors = isCustomColor ? colorType : undefined;

  const cName = classNameBind([
    'event-brand-sub',
    !isCustomColor ? `bg-type-${colorType}` : '',
  ]);

  const id = useId();
  const safeId = id.replace(/:/g, '');
  const uniqueKeyProductList = useMemo(
    () => `product-list-swiper-${safeId}`,
    [safeId]
  );
  const swiperRef = useRef<SwiperClass>(null);
  const subBrandSwiperRef = useRef<SwiperClass | null>(null);
  const subBrandSlideTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);

  const selectedIndex = useMemo(() => {
    if (!Array.isArray(subBrandList) || selectedTabId === undefined)
      return undefined;
    const idx = subBrandList.findIndex(
      (item: { props?: { value?: string | number } } | null) =>
        item?.props?.value != null &&
        String(item.props.value) === String(selectedTabId)
    );
    return idx === -1 ? undefined : idx;
  }, [subBrandList, selectedTabId]);

  const slideSubBrandTabToSelected = useCallback(
    (swiper: SwiperClass) => {
      if (swiper.destroyed || !swiper.params) return;
      if (selectedIndex === undefined) return;

      const slideToSelected = (): void => {
        if (swiper.destroyed || !swiper.params) return;
        swiper.slideTo(selectedIndex, 300, false);
        swiper.updateSlides();
        swiper.updateSize();
        swiper.update();
      };

      if (subBrandSlideTimeoutRef.current) {
        clearTimeout(subBrandSlideTimeoutRef.current);
      }
      subBrandSlideTimeoutRef.current = setTimeout(slideToSelected, 100);
    },
    [selectedIndex]
  );

  useEffect(
    () => () => {
      if (subBrandSlideTimeoutRef.current) {
        clearTimeout(subBrandSlideTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (subBrandSwiperRef.current) {
      slideSubBrandTabToSelected(subBrandSwiperRef.current);
    }
  }, [selectedTabId, subBrandList, slideSubBrandTabToSelected]);

  const derivedBgType = isCustomColor ? 'black' : colorType;

  let couponColorType: CustomColorType | 'black' | 'gray';
  if (isCustomColor) {
    couponColorType = customColors as CustomColorType;
  } else if (derivedBgType === 'white') {
    couponColorType = 'black';
  } else {
    couponColorType = 'gray';
  }

  useEffect(() => {
    if (swiperRef.current && isDesktop) {
      setTimeout(() => {
        if (swiperRef.current) {
          swiperRef.current.update();
          if (swiperRef.current.navigation) {
            swiperRef.current.navigation.destroy();
            swiperRef.current.navigation.init();
            swiperRef.current.navigation.update();
          }
        }
      }, 0);
    }
  }, [selectedTabId, isDesktop, productList]);

  const renderBrandBanner = () =>
    keyVisualImgSrc && (
      <div className="brand-banner">
        <Img src={getResizeImageUrl(keyVisualImgSrc, keyVisualImgRs)} />
        {!isDesktop && (
          <Button className="btn-more-small" onClick={onClickMore}>
            자세히 보기
            <IconArrowRight09 />
          </Button>
        )}
      </div>
    );

  const renderCoupon = () =>
    couponTitle && (
      <CouponItem2
        title={couponTitle}
        desc={couponDesc}
        status={status === '3' ? 'comingsoon' : 'download'}
        colorType={couponColorType}
        onClick={onClickCoupon}
      />
    );

  const renderDesktopContent = () => {
    const productParamPc: any = {
      autoplay: false,
      loop: false,
      slidesPerView: keyVisualImgSrc ? 4 : 6,
      slidesPerGroup: keyVisualImgSrc ? 4 : 6,
      spaceBetween: 17.5,
      navigation: {
        prevEl: `.${uniqueKeyProductList} .swiper-button-prev`,
        nextEl: `.${uniqueKeyProductList} .swiper-button-next`,
      },
      navigationEl: (
        <SwiperNavigation className={uniqueKeyProductList} offsetTop={110} />
      ),
    };

    return (
      <>
        <div className="brand-container">
          {renderBrandBanner()}

          <div className="brand-right">
            {renderCoupon()}

            <div className="brand-product-list">
              {productList && (
                <>
                  <ItemsSwiper
                    {...productParamPc}
                    className="product-list"
                    onSwiper={(swiper: SwiperClass) => {
                      swiperRef.current = swiper;
                      if (brandSubRef && 'current' in brandSubRef) {
                        // eslint-disable-next-line no-param-reassign
                        brandSubRef.current = swiper;
                      }
                    }}
                  >
                    {productList}
                  </ItemsSwiper>
                </>
              )}
            </div>
          </div>
          <BasicBtn className="btn-more" text={btnText} onClick={onClickMore} />
        </div>
      </>
    );
  };

  const renderMobileContent = () => (
    <div className="brand-container">
      {renderBrandBanner()}

      {renderCoupon()}

      <div className="brand-product-list">
        <ItemsScrollBar
          perView={3.14}
          rowGap={22}
          colGap={4}
          className="product-list"
          key={selectedTabId}
        >
          {productList}
        </ItemsScrollBar>

        {/* 배너가 없으면 배너 위 자세히보기(.btn-more-small)를 붙일 곳이 없으므로
            다른 모듈처럼 상품 리스트 하단에 노출한다.
            단 미도래 탭은 MO에서 자세히보기를 노출하지 않는 정책이라 제외한다. */}
        {!keyVisualImgSrc && status !== '3' && (
          <BasicBtn className="btn-more" text={btnText} onClick={onClickMore} />
        )}
      </div>
    </div>
  );

  return (
    <S.BrandSubSection2
      className={cName}
      $bgColor={customColors?.bgColor}
      $textColor={customColors?.textColor}
    >
      {subBrandList &&
        isSubBrandTabReady &&
        (isDesktop ? (
          <ItemsSwiper
            slidesPerView="auto"
            spaceBetween={16}
            loop={false}
            className="sub-brand-list"
            onSwiper={(swiper: SwiperClass) => {
              subBrandSwiperRef.current = swiper;
              slideSubBrandTabToSelected(swiper);
            }}
          >
            {subBrandList}
          </ItemsSwiper>
        ) : (
          <ItemsScrollBar
            key={scrollResetKey}
            perView={0}
            colGap={8}
            className="sub-brand-list"
            selectedIndex={selectedIndex}
          >
            {subBrandList}
          </ItemsScrollBar>
        ))}

      {isDesktop ? renderDesktopContent() : renderMobileContent()}
    </S.BrandSubSection2>
  );
};

export default memo(BrandSubSection2);
